// src/components/ClinicUserManagement/ClinicUserInfo.jsx
import React, { useEffect, useState } from 'react';
import Menu from '../Menu/Menu';
import api from '../../utils/axios.secure';
import { useNavigate, useParams } from 'react-router-dom';

// 토스트 훅
const useToast = () => {
  const [msg, setMsg] = useState(null);
  const toast = (m) => { setMsg(m); setTimeout(() => setMsg(null), 2200); };
  return { msg, toast };
};

// 서버 응답 OK 판별(래핑 케이스 포함)
const isOk = (res) =>
  !!(
    res?.status === true ||
    res?.ok === true ||
    (res?.messageCode === 200 && (res?.data?.status || res?.data?.ok)) ||
    (res?.data?.data && (res.data.data.status || res.data.data.ok))
  );

export default function ClinicUserInfo() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { msg, toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [exists, setExists] = useState(false); // 저장 이력 존재 여부
  const [form, setForm] = useState({
    name: '',
    birth: '',   // yyyy-mm-dd (UI 입력용) — 전송 시 age로 키만 변경
    gender: '',  // 'M' / 'F'
  });

  // 개인정보 조회
  const fetchInfo = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/manager/user/clinic/users/info/get', { user_id: userId });
      if (isOk(data)) {
        const payload = data?.data?.data || data?.data || {};
        // 서버는 name, age(=yyyy-mm-dd), gender만 보관한다고 가정
        setForm({
          name:   payload.name   || '',
          birth:  payload.age    || '',
          gender: payload.gender || '',
        });
        setExists(true);
      } else {
        setExists(false);
        setForm({ name: '', birth: '', gender: '' });
      }
    } catch (e) {
      if (e?.response?.status === 404) { // 미존재 시 신규 입력
        setExists(false);
        setForm({ name: '', birth: '', gender: '' });
      } else {
        toast('개인정보 조회 실패');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInfo(); /* eslint-disable-next-line */ }, [userId]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // yyyy-mm-dd 형식 검증 (간단)
  const isDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || '').trim());

  // 저장/수정
  const onSubmit = async () => {
    const { name, birth, gender } = form;
    if (!name?.trim())  return toast('이름을 입력하세요');
    if (!birth?.trim()) return toast('생년월일을 입력하세요');
    if (!isDate(birth)) return toast('생년월일은 yyyy-mm-dd 형식으로 입력하세요');
    if (!gender)        return toast('성별을 선택하세요');

    setLoading(true);
    try {
      // 서버는 age로 받으므로 키만 변경해서 전송
      const payload = {
        user_id: userId,
        name: name.trim(),
        age: birth.trim(),   // 생년월일
        gender,              // 'M' 또는 'F'
      };

      const { data } = exists
        ? await api.post('/manager/user/clinic/users/info/update', payload)
        : await api.post('/manager/user/clinic/users/info/save',   payload);

      if (isOk(data)) {
        toast(exists ? '수정 완료' : '저장 완료');
        setExists(true);
        // 서버 기준으로 다시 싱크
        await fetchInfo();
      } else {
        toast(exists ? '수정 실패' : '저장 실패');
      }
    } catch (e) {
      if (e?.response?.status === 400)      toast('입력값을 확인하세요');
      else if (e?.response?.status === 401) toast('세션 만료. 다시 로그인 해주세요.');
      else if (e?.response?.status === 403) toast('권한이 없습니다.');
      else                                   toast('처리 실패');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Menu>
      <div className="p-6 space-y-6 max-w-3xl">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">개인정보 관리</h1>
          <button className="px-3 py-1 rounded border" onClick={() => navigate(-1)}>
            ← 목록으로
          </button>
        </div>

        <section className="border rounded p-4 space-y-4">
          <div className="text-sm text-gray-600">
            대상 user_id: <span className="font-mono">{userId}</span>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <label className="block">
              <div className="mb-1 text-sm font-medium">이름</div>
              <input
                className="border rounded px-3 py-2 w-full"
                placeholder="예) 홍길동"
                value={form.name}
                onChange={onChange('name')}
              />
            </label>

            <label className="block">
              <div className="mb-1 text-sm font-medium">생년월일</div>
              <input
                type="date"
                className="border rounded px-3 py-2 w-full"
                value={form.birth}
                onChange={onChange('birth')}
              />
            </label>

            <label className="block">
              <div className="mb-1 text-sm font-medium">성별</div>
              <select
                className="border rounded px-3 py-2 w-full"
                value={form.gender}
                onChange={onChange('gender')}
              >
                <option value="">선택하세요</option>
                <option value="M">남자</option>
                <option value="F">여자</option>
              </select>
            </label>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onSubmit}
              disabled={loading}
              className="px-4 py-2 rounded border text-black disabled:opacity-50"
            >
              {exists ? '수정' : '저장'}
            </button>
            <button
              type="button"
              onClick={fetchInfo}
              disabled={loading}
              className="px-4 py-2 rounded border"
            >
              새로고침
            </button>
          </div>
        </section>
      </div>

      {msg && (
        <div className="fixed bottom-4 right-4 z-50 text-sm px-3 py-2 rounded bg-black/80 text-black shadow">
          {msg}
        </div>
      )}
    </Menu>
  );
}
